import {
  Box,
  FormSearchInput,
  HeaderTabLink,
  Icon,
  Line
} from "@prismicio/editor-ui"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"

import { releasesTabFlag } from "../../app/experiments"
import {
  headerHeight,
  mainContentMaxWidth,
  sidePanelWidth
} from "../../document/documentLayout"
import { DocumentSearchDialog } from "../DocumentSearchDialog"
import { ReleasesDropdownMenu } from "../releases/ReleasesDropdownMenu"
import { useSearchDocumentParams } from "../useDocumentList"
import { EnvironmentsDropdownMenu } from "./EnvironmentsDropdownMenu"
import { RepositoryTitle } from "./RepositoryTitle"

type Tab = "working" | "archived" | "upcoming"

interface DocumentListHeaderProps {
  tab: Tab | undefined
}

export function DocumentListHeader(props: DocumentListHeaderProps) {
  const { tab } = props

  return (
    <Box
      display="flex"
      flexDirection="column"
      position="fixed"
      top={0}
      height={headerHeight}
      border={{ bottom: true }}
      backgroundColor="grey2"
      minWidth="100%"
      width="100vw"
    >
      <Line color="purple" />
      <Box justifyContent="center" flexGrow={1}>
        <Box
          width={mainContentMaxWidth}
          justifyContent="center"
          alignItems="center"
          gap={24}
        >
          <Box padding={{ bottom: 12, top: 12 }}>
            <RepositoryDetails />
          </Box>
          <HeaderNavigationBar tab={tab} />
          <Box padding={{ bottom: 12, top: 12 }} flexGrow={1}>
            <SearchBar />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

function RepositoryDetails() {
  return (
    <Box gap={16} alignItems="center" minWidth={sidePanelWidth}>
      <Box backgroundColor="grey5" borderRadius={6} padding={8}>
        <Icon name="prismic" color="grey12" size="large" />
      </Box>
      <Box flexDirection="column">
        <RepositoryTitle />
        <EnvironmentsDropdownMenu />
      </Box>
    </Box>
  )
}

interface HeaderNavigationBarProps {
  tab: Tab | undefined
}

function HeaderNavigationBar(props: HeaderNavigationBarProps) {
  const { tab } = props

  const { urlSearchParams } = useSearchDocumentParams()
  const releasesFlag = releasesTabFlag() === "on"

  return (
    <Box gap={16} height="100%" position="relative" left={-8}>
      <HeaderTabLink
        text="Work"
        renderLink={(children) => (
          <Link to={`/working${urlSearchParams}`}>{children}</Link>
        )}
        selected={tab === "working"}
      />
      <HeaderTabLink
        text="Planned"
        renderLink={(children) =>
          releasesFlag ? (
            <Link to={`/upcoming${urlSearchParams}`}>
              <ReleasesDropdownMenu>{children}</ReleasesDropdownMenu>
            </Link>
          ) : (
            children
          )
        }
        disabled={!releasesFlag}
        selected={tab === "upcoming"}
      />
      <HeaderTabLink
        text="Archived"
        renderLink={(children) => (
          <Link to={`/archived${urlSearchParams}`}>{children}</Link>
        )}
        selected={tab === "archived"}
      />
    </Box>
  )
}

function SearchBar() {
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false)

  useEffect(() => {
    function keyDownHandler(event: KeyboardEvent) {
      if (event.metaKey && event.key === "k" && !isSearchDialogOpen) {
        setIsSearchDialogOpen(true)
      }
    }

    window.addEventListener("keydown", keyDownHandler)
    return () => window.removeEventListener("keydown", keyDownHandler)
  }, [isSearchDialogOpen])

  return (
    <>
      <Box width="100%" flexDirection="column">
        <FormSearchInput
          value=""
          placeholder="Search"
          onValueChange={() => {}}
          onFocus={() => setIsSearchDialogOpen(true)}
        />
      </Box>
      <DocumentSearchDialog
        isOpen={isSearchDialogOpen}
        onOpenChange={setIsSearchDialogOpen}
      />
    </>
  )
}
